import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/ClerkAuthContext";
import ReactMarkdown from "react-markdown";
import { apiService } from "../../services/api";

interface EvalItem {
  id: string;
  conversation_id: string;
  conversation_title: string;
  org_slug: string;
  app_slug?: string;
  status: 'pending' | 'in_progress' | 'complete' | 'failed';
  summary?: string;
  processing_time_ms?: number;
  started_at?: string;
}

interface EvalStatus {
  id: string;
  status: string;
  created_at: string;
  completed_at?: string;
  prompt_template?: string;
  custom_prompt?: string;
  concurrent_limit: number;
}

export const EvalExecution: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [evalStatus, setEvalStatus] = useState<EvalStatus | null>(null);
  const [items, setItems] = useState<EvalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Load initial data and start polling
  useEffect(() => {
    isMountedRef.current = true;
    loadInitialData();
    
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [id]);

  const loadInitialData = async () => {
    try {
      const token = await getToken();
      
      // Load evaluation metadata
      const evalResponse = await apiService.fetch(
        `/api/admin/conversation-evals/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      let currentEval = null;
      if (evalResponse.ok) {
        currentEval = await evalResponse.json();
        setEvalStatus(currentEval);
        
        // Start the evaluation if it's pending
        if (currentEval.status === 'pending') {
          await startEvaluation();
        }
      }
      
      // Initial load of eval status
      await fetchEvalStatus();
      
      // Start polling immediately
      startPolling();
      
      setLoading(false);
    } catch (error) {
      console.error("Failed to load evaluation:", error);
      setLoading(false);
    }
  };

  const startPolling = () => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(() => {
      if (isMountedRef.current) {
        fetchEvalStatus();
      }
    }, 2000);
  };

  const fetchEvalStatus = async () => {
    try {
      const token = await getToken();
      
      const response = await apiService.fetch(
        `/api/admin/conversation-evals/${id}/status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (!response.ok) {
        console.error("Failed to fetch status:", response.status);
        return;
      }
      
      const data = await response.json();
      
      // Only update if component is still mounted
      if (!isMountedRef.current) return;
      
      // Update state with new data
      setEvalStatus({
        id: data.id,
        status: data.status,
        created_at: data.created_at,
        concurrent_limit: 3, // Default value since not in response
      });
      
      setItems(data.items || []);
      
      // Stop polling if evaluation is complete
      if (data.status === 'completed' || data.status === 'failed') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  const startEvaluation = async () => {
    try {
      const token = await getToken();
      const response = await apiService.fetch(
        `/api/admin/conversation-evals/${id}/start`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (!response.ok) {
        console.error("Failed to start evaluation:", response.status);
      }
    } catch (error) {
      console.error("Failed to start evaluation:", error);
    }
  };

  // Optional: Handle visibility changes to pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden - stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else {
        // Page visible - resume polling if not complete
        if (!pollingIntervalRef.current && 
            evalStatus?.status !== 'completed' && 
            evalStatus?.status !== 'failed') {
          startPolling();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [evalStatus]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'in_progress': return '🔄';
      case 'complete': return '✅';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  const getElapsedTime = (startedAt?: string) => {
    if (!startedAt) return null;
    const elapsed = Date.now() - new Date(startedAt).getTime();
    return `${Math.floor(elapsed / 1000)}s`;
  };

  const calculateProgress = () => {
    const completed = items.filter(item => 
      item.status === 'complete' || item.status === 'failed'
    ).length;
    return items.length > 0 ? (completed / items.length) * 100 : 0;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Evaluation Execution
          </h1>
          <button
            onClick={() => navigate('/admin/evals')}
            className="text-gray-600 hover:text-gray-900"
          >
            Back to Evaluations
          </button>
        </div>
        
        {evalStatus && (
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">ID:</span>
                <span className="ml-2 font-mono text-xs">{evalStatus.id}</span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <span className="ml-2 font-semibold">{evalStatus.status}</span>
              </div>
              <div>
                <span className="text-gray-500">Template:</span>
                <span className="ml-2">{evalStatus.prompt_template || 'Custom'}</span>
              </div>
              <div>
                <span className="text-gray-500">Concurrency:</span>
                <span className="ml-2">{evalStatus.concurrent_limit}</span>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Overall Progress
            </span>
            <span className="text-sm text-gray-500">
              {items.filter(i => i.status === 'complete' || i.status === 'failed').length} / {items.length} complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Conversation Items</h2>
        
        {items.map(item => (
          <div 
            key={item.id}
            className={`bg-white p-4 rounded-lg shadow transition-all ${
              item.status === 'in_progress' ? 'ring-2 ring-indigo-500' : ''
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center">
                <span className="text-2xl mr-3">{getStatusIcon(item.status)}</span>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {item.conversation_title || 'Untitled Conversation'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {item.org_slug}
                    {item.app_slug && ` • ${item.app_slug}`}
                  </p>
                </div>
              </div>
              
              <div className="text-right text-sm text-gray-500">
                {item.status === 'in_progress' && item.started_at && (
                  <span className="font-mono">{getElapsedTime(item.started_at)}</span>
                )}
                {item.status === 'complete' && item.processing_time_ms && (
                  <span className="font-mono">{(item.processing_time_ms / 1000).toFixed(1)}s</span>
                )}
              </div>
            </div>
            
            {item.status === 'in_progress' && (
              <div className="mt-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            )}
            
            {item.summary && (
              <div className={`mt-3 p-3 rounded bg-gray-50 ${
                item.status === 'failed' ? 'bg-red-50' : ''
              }`}>
                <div className={`text-sm prose prose-sm max-w-none ${
                  item.status === 'failed' ? 'text-red-700' : 'text-gray-700'
                }`}>
                  <ReactMarkdown>{item.summary}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {evalStatus?.status === 'completed' && (
        <div className="mt-6 flex justify-center space-x-4">
          <button
            onClick={async () => {
              const token = await getToken();
              if (!token) return;
              
              const response = await fetch(`/api/admin/conversation-evals/${id}/results/raw`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });
              
              if (response.ok) {
                const text = await response.text();
                const blob = new Blob([text], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const newWindow = window.open(url, '_blank');
                
                // Force the browser to display as text, not download
                if (newWindow) {
                  newWindow.location = url;
                }
                
                // Clean up after a delay
                setTimeout(() => URL.revokeObjectURL(url), 10000);
              }
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            View Full Results
          </button>
          <button
            onClick={() => navigate('/admin/evals')}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            Back to Evaluations
          </button>
        </div>
      )}
    </div>
  );
};